/**
 * Date/time formatting — single source for rendering stored UTC timestamps in a
 * workshop's configured timezone (Tenant localization).
 *
 * Backend datetimes are stored tz-aware UTC (``timestamptz``) and serialized as
 * ISO-8601 strings with an offset. These helpers parse that instant and render it
 * in an explicit ``timeZone`` via ``Intl.DateTimeFormat`` — so the displayed time
 * matches the workshop's wall clock regardless of the viewer's browser timezone.
 *
 * Pure + param-taking (mirrors ``lib/money``): callers in the Tenant app bind the
 * options via the ``useFormat()`` hook; non-React callers (jsPDF, utils) pass the
 * options explicitly.
 *
 * IMPORTANT: these are for FULL timestamps only. Date-only ``YYYY-MM-DD`` strings
 * (filters, pickers) must NOT be tz-shifted — keep using ``formatDisplayYmd`` from
 * ``calendarShared``. Nullish/invalid input renders the em-dash sentinel; never
 * throws (these run in render) and never substitutes "now".
 */

/** Rendered for null/empty/invalid input. Matches the codebase '—' convention. */
export const DATE_SENTINEL = '—';

export interface DateFmtOpts {
  /** IANA timezone (e.g. 'Europe/Madrid', 'America/New_York'). */
  timeZone?: string;
  /** BCP-47 locale for ordering/separators (e.g. 'es-ES', 'en-GB'). */
  locale?: string;
}

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date + time, e.g. `08/06/2026, 14:32`. */
export function formatDateTime(iso: string | null | undefined, opts: DateFmtOpts = {}): string {
  const d = parseInstant(iso);
  if (!d) return DATE_SENTINEL;
  return new Intl.DateTimeFormat(opts.locale || 'es-ES', {
    timeZone: opts.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Date only, e.g. `08/06/2026`. */
export function formatDate(iso: string | null | undefined, opts: DateFmtOpts = {}): string {
  const d = parseInstant(iso);
  if (!d) return DATE_SENTINEL;
  return new Intl.DateTimeFormat(opts.locale || 'es-ES', {
    timeZone: opts.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Flexible formatter — applies the given ``Intl.DateTimeFormat`` options in the
 * configured timezone/locale. Use to preserve a site's bespoke format (e.g. a
 * short month name) while gaining timezone-awareness. Nullish/invalid → '—'.
 */
export function formatDateWith(
    iso: string | null | undefined,
    opts: DateFmtOpts & Intl.DateTimeFormatOptions = {},
): string {
    const d = parseInstant(iso);
    if (!d) return DATE_SENTINEL;
    const { locale, ...rest } = opts;
    return new Intl.DateTimeFormat(locale || 'es-ES', rest).format(d);
}

/** Time only, e.g. `14:32`. */
export function formatTime(iso: string | null | undefined, opts: DateFmtOpts = {}): string {
  const d = parseInstant(iso);
  if (!d) return DATE_SENTINEL;
  return new Intl.DateTimeFormat(opts.locale || 'es-ES', {
    timeZone: opts.timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * The calendar day (``YYYY-MM-DD``) of an instant in the given timezone. Use for
 * day-granularity comparisons so "today/overdue" is computed against the workshop's
 * wall clock, not the browser's. ``en-CA`` renders ISO-like ``YYYY-MM-DD``.
 */
export function ymdInTz(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** The workshop's "today" as ``YYYY-MM-DD`` in the given timezone. */
export function tenantTodayYmd(opts: DateFmtOpts = {}): string {
  return ymdInTz(new Date(), opts.timeZone);
}
